import {QueryClient} from '@tanstack/react-query';

import {ICategory} from 'coreStore/data/categories/types';
import {absoluteUrl, removeCountryPrefix} from 'coreUtils/url';

import {getCategoriesQueryKey} from './categoriesService';

export interface ICategoryResult {
	category: ICategory | null;
	getId: () => string | undefined;
	getName: () => string | undefined;
	getType: () => string | undefined;
	getDetail: () => string | undefined;
	getCategoryDescription: () => string | undefined;
	getCategoryUrl: () => string | null;
	getUrl: () => string | null;
	getPlainUrl: () => string | null;
	getParentCategoryId: () => string | undefined;
	getParentCategoryTreeIds: () => string[] | undefined;
	getParentCategoryTree: () => ICategoryResult[];
	isCGIDInParentCategoryTree: (cgid: string) => boolean;
	isLABCategory: () => boolean;
	isBrandCategory: () => boolean;
	isLaminarCategory: () => boolean;
	getSizeGuideId: () => string | undefined;
	getSizeGuideIds: () => string;
	isShowDifferentImageView: () => boolean | undefined;
	isShowInSearchTabs: () => boolean | undefined;
	getAltImageIndex: () => string | undefined;
	getCanonicalLink: () => string | undefined;
	isNoIndex: () => boolean | undefined;
	isExcludedFromSiteMapPage: () => boolean | undefined;
	getSubCategories: () => ICategoryResult[];
	getPlpPageTitle: () => string;
	getTabTitle: () => string;
	getCategorySpecificPartText: () => string;
	isMeasmerizeButtonEnabled: () => boolean;
	getShowDiscountPercentage: () => boolean | undefined;
	getShowCarouselHits: () => boolean;
	isDefaultPLPTemplate: () => boolean;
	isDotsFilterEnabled: () => boolean;
	getDotsConfiguration: () => string;
	getDotsFilterMatch: () => string;
	getMatchItWithCategoryIds: () => string;
	getLinksConfiguration: () => string[];
	getCrossLinkCategoryIds: () => string[] | undefined;
	isRequiresLogin: () => boolean;
	isRequiresLoginInHierarchy: () => boolean;
	isChangeViewButtonEnabled: () => boolean;
	getPlpDefaultViewType: () => string | undefined;
	isTilesMouseHoverEnabled: () => boolean;
	getAlternativeImageIndex: () => number | null;
}

export interface CategoryDeps {
	localesMgr: {updateLinkWithLocale: (url: string) => string};
	isCORSRequest: boolean;
	queryClient: QueryClient;
	languageCode: string;
}

/** Wraps a raw {@link ICategory} into an {@link ICategoryResult} accessor object. */
export function createCategoryResult(
	cat: ICategory | null,
	{localesMgr, isCORSRequest, queryClient, languageCode}: CategoryDeps,
): ICategoryResult {
	const resolvedCat = cat?._notFound ? null : cat;

	const getId = (): string | undefined => resolvedCat?.id;

	const getName = (): string | undefined => resolvedCat?.name;

	const getType = (): string | undefined => resolvedCat?.type;

	const getDetail = (): string | undefined => resolvedCat?.detail;

	const getCategoryDescription = (): string | undefined => resolvedCat?.pageDescription;

	const getCategoryUrl = (): string | null => {
		if (!resolvedCat) {
			return null;
		}

		const categoryUrl = removeCountryPrefix(resolvedCat.c_url);
		const url = localesMgr.updateLinkWithLocale(categoryUrl);

		return isCORSRequest ? absoluteUrl(url) : url;
	};

	const getPlainUrl = (): string | null => resolvedCat?.c_url ?? null;

	const getParentCategoryId = (): string | undefined => resolvedCat?.parentCategoryId;

	const getParentCategoryTreeIds = (): string[] | undefined => resolvedCat?.parentCategoryTree;

	const getParentCategoryTree = (): ICategoryResult[] => {
		if (!resolvedCat?.parentCategoryTree) {
			return [];
		}

		const allCategories =
			queryClient.getQueryData<Record<string, ICategory>>(getCategoriesQueryKey(languageCode)) || {};

		return resolvedCat.parentCategoryTree.reduce<ICategoryResult[]>((arr, cgid) => {
			const stored = allCategories[cgid];

			if (stored && !stored._notFound) {
				arr.push(
					createCategoryResult(stored, {
						localesMgr,
						isCORSRequest,
						queryClient,
						languageCode,
					}),
				);
			}

			return arr;
		}, []);
	};

	const isCGIDInParentCategoryTree = (cgid: string): boolean => {
		const parentTree = getParentCategoryTree();

		return parentTree.some((parent) => parent.getId()?.toLowerCase() === cgid.toLowerCase());
	};

	const isLABCategory = (): boolean => isCGIDInParentCategoryTree('lab');

	const isLaminarCategory = (): boolean => isCGIDInParentCategoryTree('laminar');

	const isBrandCategory = (): boolean => isCGIDInParentCategoryTree('brands');

	const getSizeGuideId = (): string | undefined => resolvedCat?.c_sizeGuide;

	const getSubCategories = (): ICategoryResult[] => {
		if (!resolvedCat?.categories) {
			return [];
		}

		const allCategories =
			queryClient.getQueryData<Record<string, ICategory>>(getCategoriesQueryKey(languageCode)) || {};

		return resolvedCat.categories.reduce<ICategoryResult[]>((arr, cgid) => {
			const stored = allCategories[cgid];

			if (stored && !stored._notFound) {
				arr.push(
					createCategoryResult(stored, {
						localesMgr,
						isCORSRequest,
						queryClient,
						languageCode,
					}),
				);
			}

			return arr;
		}, []);
	};

	const getSizeGuideIds = (): string => {
		if (!resolvedCat) {
			return '';
		}

		let current: ICategory | undefined = resolvedCat;
		let sizeGuideId = resolvedCat.c_sizeGuide;
		const allCategories =
			queryClient.getQueryData<Record<string, ICategory>>(getCategoriesQueryKey(languageCode)) || {};

		while (!sizeGuideId && current?.parentCategoryId) {
			current = allCategories[current.parentCategoryId];

			if (current?._notFound) {
				break;
			}

			sizeGuideId = current?.c_sizeGuide;
		}

		return sizeGuideId || '';
	};

	const isShowDifferentImageView = (): boolean | undefined => resolvedCat?.c_showDifferentImageView;

	const isShowInSearchTabs = (): boolean | undefined => resolvedCat?.c_showInSearchTabs;

	const getAltImageIndex = (): string | undefined => resolvedCat?.c_altImageIndex;

	const getCanonicalLink = (): string | undefined => resolvedCat?.c_canonicalLink;

	const isNoIndex = (): boolean | undefined => resolvedCat?.c_noindex;

	const isExcludedFromSiteMapPage = (): boolean | undefined => resolvedCat?.c_isExcludedFromSiteMapPage;

	const getPlpPageTitle = (): string => resolvedCat?.c_plpPageTitle || '';

	const getTabTitle = (): string => resolvedCat?.c_tabTitle || '';

	const getCategorySpecificPartText = (): string => resolvedCat?.c_categorySpecificPartText || '';

	const isMeasmerizeButtonEnabled = (): boolean => {
		if (!resolvedCat) {
			return false;
		}

		let current: ICategory | undefined = resolvedCat;
		let isEnabled = resolvedCat.c_enableMeasmerizeButton;
		const allCategories =
			queryClient.getQueryData<Record<string, ICategory>>(getCategoriesQueryKey(languageCode)) || {};

		while (!isEnabled && current?.parentCategoryId) {
			current = allCategories[current.parentCategoryId];

			if (current?._notFound) {
				break;
			}

			isEnabled = current?.c_enableMeasmerizeButton;
		}

		return !!isEnabled;
	};

	const getShowDiscountPercentage = (): boolean | undefined => resolvedCat?.c_showDiscountPercentage;

	const getShowCarouselHits = (): boolean => resolvedCat?.c_showCarouselHits || false;

	const isDefaultPLPTemplate = (): boolean => !!resolvedCat?.c_isDefaultPLPTemplate;

	const isDotsFilterEnabled = (): boolean => !!resolvedCat?.c_enableDotsFilter;

	const getDotsConfiguration = (): string => resolvedCat?.c_dotsConfiguration || '';

	const getDotsFilterMatch = (): string => resolvedCat?.c_dotsFilterMatch || '';

	const getMatchItWithCategoryIds = (): string => resolvedCat?.c_matchItWithCategoryIds ?? '';

	const getLinksConfiguration = (): string[] => resolvedCat?.c_linksConfiguration ?? [];

	const getCrossLinkCategoryIds = (): string[] | undefined => resolvedCat?.c_crossLinkCategoryIds;

	const isRequiresLogin = (): boolean => resolvedCat?.c_requiresLogin || false;

	const isRequiresLoginInHierarchy = (): boolean => {
		if (resolvedCat?.c_requiresLogin) {
			return true;
		}

		const parentTree = getParentCategoryTree();

		return parentTree.some((parent) => parent.isRequiresLogin());
	};

	const isChangeViewButtonEnabled = (): boolean => !!resolvedCat?.c_enableChangeViewTypeButton;

	const getPlpDefaultViewType = (): string | undefined => resolvedCat?.c_plpDefaultViewType;

	const isTilesMouseHoverEnabled = (): boolean => !!resolvedCat?.c_enableTilesMouseHover;

	const getAlternativeImageIndex = (): number | null => Number(resolvedCat?.c_altImageIndex) || null;

	return {
		category: resolvedCat,
		getId,
		getName,
		getType,
		getDetail,
		getCategoryDescription,
		getCategoryUrl,
		getUrl: getCategoryUrl,
		getPlainUrl,
		getParentCategoryId,
		getParentCategoryTreeIds,
		getParentCategoryTree,
		isCGIDInParentCategoryTree,
		isLABCategory,
		isBrandCategory,
		isLaminarCategory,
		getSizeGuideId,
		getSizeGuideIds,
		isShowDifferentImageView,
		isShowInSearchTabs,
		getAltImageIndex,
		getCanonicalLink,
		isNoIndex,
		isExcludedFromSiteMapPage,
		getSubCategories,
		getPlpPageTitle,
		getTabTitle,
		getCategorySpecificPartText,
		isMeasmerizeButtonEnabled,
		getShowDiscountPercentage,
		getShowCarouselHits,
		isDefaultPLPTemplate,
		isDotsFilterEnabled,
		getDotsConfiguration,
		getDotsFilterMatch,
		getMatchItWithCategoryIds,
		getLinksConfiguration,
		getCrossLinkCategoryIds,
		isRequiresLogin,
		isRequiresLoginInHierarchy,
		isChangeViewButtonEnabled,
		getPlpDefaultViewType,
		isTilesMouseHoverEnabled,
		getAlternativeImageIndex,
	};
}
